import type { ComboboxItem, ComboboxItemGroup } from '@any-ui-react/core';
import groupBy from 'lodash-es/groupBy';
import map from 'lodash-es/map';
import { useMemo } from 'react';
import { Controller } from 'react-hook-form';
import { isGroup } from '@/common/components/shared/select/group';
import { EpmVdpSourceType, useListMinimalCompaniesQuery } from '@/common/graphql';
import { getVdpTotal, MAX_VDP_ENTRIES, type VdpSuggestion } from '@/common/types';
import { AllocationField } from './AllocationField';
import { AllocationPercentageInput } from './AllocationPercentageInput';
import { AllocationSelectInput } from './AllocationSelectInput';
import type { AllocationFieldWithSuggestionProps, SuggestionAppend } from './props';
import { SuggestionHint } from './SuggestionHint';

const REGIONAL_VALUE = '__REGIONAL__';

interface VdpSuggestionAppend extends SuggestionAppend {
  sourceType: EpmVdpSourceType;
  companyId: string | null;
}

export const AllocationFieldVdp = ({
  control,
  errors,
  fields,
  append,
  remove,
  watched,
  suggestions,
  showSuggestionHint,
  register,
  setValue,
}: AllocationFieldWithSuggestionProps<'vdp', VdpSuggestion, VdpSuggestionAppend>) => {
  const total = useMemo(() => getVdpTotal(watched), [watched]);

  const { data: companiesData, loading } = useListMinimalCompaniesQuery({
    variables: {
      pageNumber: null,
      pageSize: null,
      keyword: null,
      enabled: true,
      filterOnlyAccessible: null,
      hasEmployees: true,
    },
    fetchPolicy: 'cache-first',
  });

  const options: Array<ComboboxItem | ComboboxItemGroup> = useMemo(
    () => [
      { value: REGIONAL_VALUE, label: 'Regional' } as ComboboxItem,
      ...map(
        groupBy(companiesData?.listCompanies.items, 'countryId'),
        items =>
          ({
            group: items[0].country.name,
            items: items.map(({ id: value, name: label, enabled }) => ({ value, label, disabled: !enabled })),
          }) as ComboboxItemGroup,
      ),
    ],
    [companiesData?.listCompanies.items],
  );

  return (
    <AllocationField
      addLabel="Add VDP"
      errors={errors}
      fields={fields}
      formKey="vdp"
      loading={loading}
      maxEntries={MAX_VDP_ENTRIES}
      title="VDP (Value Delivered Place)"
      total={total}
      onAdd={() => append({ sourceType: null as unknown as EpmVdpSourceType, companyId: null, percentage: 0 })}
      onRemove={remove}
      suggestionHint={
        <SuggestionHint
          show={showSuggestionHint}
          suggestions={suggestions}
          watched={watched}
          isItemDifferent={(w, s) => w.sourceType !== s.sourceType || (w.companyId ?? null) !== (s.companyId ?? null)}
          renderName={s => (s.sourceType === EpmVdpSourceType.Regional ? 'Regional' : s.companyName)}
          getTooltipSources={s => s.suggestionSourceNames.filter(it => it !== s.companyName)}
        />
      }
      renderRowContent={i => {
        const alreadySelected = watched
          .slice(0, i)
          .map(it => (it.sourceType === EpmVdpSourceType.Regional ? REGIONAL_VALUE : it.companyId));
        const remainingOptions = options.reduce<Array<ComboboxItem | ComboboxItemGroup>>((acc, it) => {
          if (isGroup(it)) {
            const items = it.items.filter(item => {
              const val = typeof item === 'string' ? item : item.value;
              return !alreadySelected.includes(val);
            });
            if (items.length > 0) acc.push({ ...it, items });
          } else if (!alreadySelected.includes(it.value)) {
            acc.push(it);
          }
          return acc;
        }, []);
        return (
          <>
            <Controller
              control={control}
              name={`vdp.${i}.sourceType`}
              render={({ field }) => {
                const companyId = watched[i]?.companyId;
                const selectValue =
                  field.value === null
                    ? null
                    : field.value === EpmVdpSourceType.Regional
                      ? REGIONAL_VALUE
                      : (companyId ?? null);
                return (
                  <AllocationSelectInput
                    data={remainingOptions}
                    disabled={loading}
                    placeholder="Company or 'Regional'"
                    value={selectValue}
                    onChange={val => {
                      if (!val) {
                        field.onChange(null as unknown as EpmVdpSourceType);
                        setValue(`vdp.${i}.companyId`, null);
                        return;
                      }
                      const isRegional = val === REGIONAL_VALUE;
                      field.onChange(isRegional ? EpmVdpSourceType.Regional : EpmVdpSourceType.Company);
                      setValue(`vdp.${i}.companyId`, isRegional ? null : val);
                    }}
                  />
                );
              }}
            />
            <AllocationPercentageInput
              registration={register(`vdp.${i}.percentage`, { valueAsNumber: true })}
              disabled={loading}
            />
          </>
        );
      }}
    />
  );
};
